import { useMemo } from "react";
import { MENU_SECTIONS, isMenuSubGroup, type MenuSection, type MenuPath } from "@/lib/menu-paths";
import { usePermissions } from "@/contexts/permission-context";
import { useAuth } from "@/contexts/auth-context";
import { useClientProfile } from "@/contexts/client-profile-context";

/** A que secção do menu pertence este caminho. Necessário para saber que
 *  módulo o cobre — a regra de acesso está no item, mas o módulo é da secção. */
function sectionOfPath(path: string): MenuSection | undefined {
  return MENU_SECTIONS.find((section) =>
    flattenSectionItems(section).some((item) => item.path === path),
  );
}

/** Flatten a section's items (unwrapping sub-groups) into a plain MenuPath array */
function flattenSectionItems(section: MenuSection): MenuPath[] {
  return section.items.flatMap((item) =>
    isMenuSubGroup(item) ? item.items : [item]
  );
}

/**
 * Menu access hook — synchronous, no API calls.
 * Access rules come directly from menu-paths.ts — no separate mapping needed.
 */
export const useMenuAccess = () => {
  const { user } = useAuth();
  const { isAdmin, hasPermission: hasCrudPermission } = usePermissions();
  const { hasFeature, hasModuleForSection, hasModule } = useClientProfile();
  const isSuperuser = !!user?.isSuperuser;

  const isRepresentative = useMemo(
    () => !!user?.departments?.some((d) => d.representatives?.some((r) => r.id === user.id)),
    [user]
  );

  const hasMenuAccess = useMemo(() => {
    return (path: string): boolean => {
      // Find the menu item definition to read its access rule
      for (const section of MENU_SECTIONS) {
        for (const item of flattenSectionItems(section)) {
          if (item.path !== path) continue;

          // GATE DE MÓDULO — antes de tudo o resto.
          //
          // Um módulo que este cliente não comprou não existe para ele: a API
          // responde 404 (ver `deps.py`), e mostrar a entrada no menu só levaria
          // a um clique que dá erro. Esconder é a única apresentação honesta.
          //
          // Um item pode declarar o seu próprio módulo, diferente do da secção
          // (ex.: Amostras Técnicas vive visualmente em "Comercial" mas exige
          // só `comercial_avancado` — ADR-0006, `comercial_avancado` não
          // depende de `comercial`). Esse override tem de ser avaliado por si
          // — testar primeiro o módulo da SECÇÃO escondia o item a uma
          // instalação com `comercial_avancado` mas sem `comercial`, apesar
          // de o override existir precisamente para esse caso.
          // "Qualquer um destes módulos" (o Calendário): some quando o perfil não tem nenhum.
          if (item.anyModule && !item.anyModule.some((m) => hasModule(m))) return false;

          if (item.module) {
            if (!hasModule(item.module)) return false;
          } else if (!hasModuleForSection(section.id)) {
            return false;
          }

          // Feature-flag do perfil: item escondido se a feature não estiver ligada
          // (ex.: Banco de Horas desligado para a Afisocks — sem "hr.time_bank").
          if (item.feature && !hasFeature(item.feature)) return false;
          const access = item.access ?? "universal";

          if (access === "universal") return true;
          if (access === "superuser") return isSuperuser;
          if (access === "admin") return isAdmin;
          if (access === "representative") return isAdmin || hasCrudPermission("users", "READ") || isRepresentative;
          // { entity, action } permission check
          return isAdmin || hasCrudPermission(access.entity, access.action);
        }
      }
      // Este ponto só é alcançado por um caminho que não bate com NENHUM item
      // do menu (o loop acima já devolveu para qualquer item encontrado,
      // override de módulo incluído) — deixa passar, os guardas de rota
      // tratam da autenticação. Mas se pertence a uma secção de um módulo
      // desligado, fecha — senão um link directo contornava o gate.
      const section = sectionOfPath(path);
      if (section && !hasModuleForSection(section.id)) return false;
      return true;
    };
  }, [isSuperuser, isAdmin, hasCrudPermission, isRepresentative, hasFeature, hasModuleForSection, hasModule]);

  const accessibleMenuSections = useMemo(() => {
    return MENU_SECTIONS.filter((section) =>
      flattenSectionItems(section).some((item) => hasMenuAccess(item.path))
    );
  }, [hasMenuAccess]);

  return { isAdmin, hasMenuAccess, accessibleMenuSections };
};
